import { BaseComponent } from '../core/BaseComponent.js';
import { RequestUploadModal } from '../pages/request-upload-modal.js';
import { ConfirmFacilityModal } from '../pages/confirm-facility-modal.js';
import { showToast } from '../utils/toast.js';
import { DatePicker } from './DatePicker.js';
import { apiClient } from '../utils/ApiClient.js';
import { Modal } from '../utils/Modal.js';
import NiceSelect from 'nice-select2';

// ── Sub-module mixins ──────────────────────────────────────────────────────────
import { ModalsMixin }   from './request-details/Modals.js';
import { ActivityMixin } from './request-details/Activity.js';
import { FilesMixin }    from './request-details/Files.js';
import { WorkflowMixin } from './request-details/Workflow.js';
import { PaymentsMixin } from './request-details/Payments.js';
import { FacilityMixin } from './request-details/Facility.js';
import { LockMixin }     from './request-details/Lock.js';

/**
 * RequestDetails — orchestrator.
 * All domain logic lives in the request-details/* mixins.
 */
export class RequestDetails extends BaseComponent {
    constructor(options = {}) {
        super(document, options);

        this.overlaySelector    = options.overlaySelector    || '.request_detail_overlay';
        this.detailsSelector    = options.detailsSelector    || '#request_detail';
        this.closeButtonSelector = options.closeButtonSelector || '.close_request_detail';
        this.triggerSelector    = options.triggerSelector    || 'tbody tr';

        this.activeRequestId = null;
        this.formSelector       = '#prepay-edit-form';
        this.PostPayformSelector = '#postpay-edit-form';
        this.noteFormSelector   = '.request-note-form';
        this.apiAction          = 'submit_prepay_edits';
        this.apiActionPostPay   = 'submit_postpay_edits';
        this.ajaxUrl            = window.docuswiftPortal?.ajax_url || '/wp-admin/admin-ajax.php';

        this.uploadModal          = null;  // fixed: no longer re-created on every open
        this.confirmFacilityModal = null;
        this.prePay               = null;
        this.postPay              = null;
        this._followUpDateUpdated = false;  // tracks whether follow-up date was changed this session
        this._skipFollowUpCheck   = false;  // bypass flag for "close anyway" action
        this.facilitySearchDebounceMs = 600;
        this._facilitySearchTimer     = null;
        this.fetchAction    = options.fetchAction    || 'get_request_detail';
        this.skeletonMarkupKey = options.skeletonMarkupKey || null;
    }

    init() {
        this.cacheElements();
        this.moveWorkflowModalsToBody();
        if (!this.overlay || !this.details) return this;
        this.setupEventListeners();

        document.addEventListener('docuswift:request-facility-applied', (e) => {
            const requestId = e?.detail?.requestId || this.activeRequestId;
            if (!requestId) return;
            this.confirmFacilityModal?.close();
            this.fetchDetails(String(requestId));
        });

        this.openFromUrlIfPresent();

        return this;
    }

    cacheElements() {
        this.overlay     = document.querySelector(this.overlaySelector);
        this.details     = document.querySelector(this.detailsSelector);
        this.closeButton = this.details?.querySelector(this.closeButtonSelector);
        this.triggers    = document.querySelectorAll(this.triggerSelector);
        this.form        = this.$(this.formSelector);
        this.postPayForm = this.$(this.PostPayformSelector);
    }

    setupEventListeners() {
        // Row click → show details
        document.addEventListener('click', (e) => {
            if (e.target.closest(this.triggerSelector)) {
                e.preventDefault();
                let requestId = e.target.closest(this.triggerSelector).dataset.requestId;
                this.show(requestId);
                this.initRequestUploadModal();
                this.initConfirmFacilityModal();
            }
        });

        // Overlay → close
        if (this.overlay) {
            this.overlay.addEventListener('click', () => this.hide());
        }

        // Close button (delegated — inside dynamically loaded content)
        document.addEventListener('click', (e) => {
            const btn = e.target.closest(this.closeButtonSelector);
            if (!btn) return;
            if (!this.details?.contains(btn)) return;
            e.preventDefault();
            this.hide();
        });

        // Escape key → close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen()) this.hide();
        });

        // Upload button
        document.addEventListener('click', (e) => {
            const uploadBtn = e.target.closest('#request_upload_btn, [data-request-upload-trigger]');
            if (!uploadBtn) return;
            e.preventDefault();
            if (!this.activeRequestId) return;
            this.openRequestUploadModal();
        });

        // Open facility details popup from sidebar Edit button
        document.addEventListener('click', (e) => {
            const el  = e.target instanceof Element ? e.target : e.target.parentElement;
            const btn = el?.closest('[data-open-facility-id]');
            if (!btn) return;
            e.preventDefault();
            e.stopPropagation();
            const facilityId = parseInt(btn.dataset.openFacilityId, 10);
            if (!facilityId) return;
            const facilityComponent = window.DocuswiftPortal?.getComponent('facility');
            if (facilityComponent) {
                facilityComponent.setActiveRequest?.(this.activeRequestId);
                facilityComponent.openEdit(facilityId);
            }
        }, true);

        // Confirm facility button (capture phase — SVG children)
        document.addEventListener('click', (e) => {
            const el = e.target instanceof Element ? e.target : e.target.parentElement;
            const facilityBtn = el?.closest('#confirm_facility_btn, [data-confirm-facility-trigger]');
            if (!facilityBtn) return;
            e.preventDefault();
            const fromBtn     = (facilityBtn.dataset.requestId || '').trim();
            const fromWrapper = (this.details?.querySelector('.request_details_wrapper')?.dataset.requestId || '').trim();
            const rid         = fromBtn || fromWrapper;
            if (!rid || rid === '-1') return;
            this.activeRequestId = rid;
            this.openConfirmFacilityModal();
        }, true);

        document.addEventListener('request:file-uploaded', (e) => {
            const requestId = e?.detail?.requestId;
            if (!requestId || String(requestId) !== String(this.activeRequestId)) return;
            this.fetchDetails(requestId);
        });

        // Edit Prepay button → open payment modal
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('.edit-prepay-button');
            if (!btn) return;
            e.preventDefault();
            this.openPaymentEditModal(btn, 'prepay');
        });

        // Edit Postpay button → open payment modal
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('.edit-postpay-button');
            if (!btn) return;
            e.preventDefault();
            this.openPaymentEditModal(btn, 'postpay');
        });

        // Payment edit modal — list item actions (delete / edit)
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#payment_edit_list')) return;
            this.handlePaymentEditListClick(e);
        });

        // Payment edit modal — form submit (add / save)
        document.addEventListener('submit', (e) => {
            if (e.target?.id !== 'payment_edit_form') return;
            this.handlePaymentEditFormSubmit(e);
        });

        // Payment edit modal — cancel button
        document.addEventListener('click', (e) => {
            if (!e.target.matches('#payment_edit_form_cancel')) return;
            e.preventDefault();
            const index = parseInt(document.getElementById('payment_edit_index')?.value ?? '-1', 10);
            if (index >= 0) {
                // Was editing an existing entry → go back to "Add" mode
                this._resetPaymentEditForm(this._paymentEditType);
            } else {
                // Was in "Add" mode → close modal
                this.closePaymentEditModal();
            }
        });

        // Payment edit modal — close via X button or overlay
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#payment_edit_modal')) return;
            if (e.target.closest('#payment_edit_form button[type="submit"]')) return;
            if (
                e.target.closest('.close_modal') ||
                e.target.matches('.overlay')
            ) {
                e.preventDefault();
                this.closePaymentEditModal();
            }
        });

        // Edit Status button → open modal
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('.edit-status-button');
            if (!btn) return;
            e.preventDefault();
            this.openEditStatusModal(btn);
        });

        // Edit Status modal → confirm
        document.addEventListener('click', (e) => {
            if (!e.target.matches('#edit_status_confirm')) return;
            e.preventDefault();
            this.submitEditStatus();
        });

        // Edit Status modal → cancel / overlay / close
        document.addEventListener('click', (e) => {
            if (
                e.target.closest('#edit_status_modal .cancel_button') ||
                e.target.closest('#edit_status_modal .close_modal') ||
                (e.target.matches('#edit_status_modal .overlay'))
            ) {
                Modal.close('edit_status_modal');
            }
        });

        // File actions select
        document.addEventListener('change', (e) => {
            const sel = e.target;
            if (!sel.matches?.('select[data-file-actions-select]')) return;
            if (!this.details?.contains(sel)) return;
            const wrap   = sel.closest('.file-actions-select-wrap');
            const action = sel.value;

            const resetSelect = () => {
                sel.selectedIndex = 0;
                const applyReset = () => {
                    const niceEl = sel.parentElement?.querySelector('.nice-select');
                    if (!niceEl) return;
                    const currentEl = niceEl.querySelector('.current');
                    if (currentEl) currentEl.textContent = sel.options[0]?.text || '';
                    niceEl.classList.remove('open');
                    niceEl.querySelectorAll('.option').forEach((opt, i) => {
                        opt.classList.toggle('selected', i === 0);
                        opt.classList.toggle('focus', i === 0);
                    });
                };
                applyReset();
                requestAnimationFrame(applyReset);
            };

            if (!action) return;
            if (action === 'download') {
                const url = wrap?.getAttribute('data-download-url') || '';
                const rid = wrap?.getAttribute('data-request-id') || String(this.activeRequestId || '');
                if (url) window.location.assign(url);
                resetSelect();
                // Downloading is a plain navigation, not an AJAX call, so refresh
                // the activity list afterward to reflect the log entry it wrote.
                if (rid) {
                    setTimeout(() => {
                        if (String(this.activeRequestId) === String(rid)) this.fetchDetails(rid);
                    }, 800);
                }
                return;
            }
            if (action === 'visibility') {
                if (wrap && this.details?.contains(wrap)) this.openFileVisibilityModal(wrap);
                resetSelect();
                return;
            }
            if (action === 'edit') {
                if (wrap && this.details?.contains(wrap)) {
                    const rid = wrap.getAttribute('data-request-id');
                    const aid = wrap.getAttribute('data-attachment-id');
                    const cat = wrap.getAttribute('data-file-category') || '';
                    const sta = wrap.getAttribute('data-file-status')   || '';
                    if (rid && aid) this.openEditFileModal(rid, aid, cat, sta);
                }
                resetSelect();
                return;
            }
            if (action === 'delete') {
                if (wrap && this.details?.contains(wrap)) {
                    const rid = wrap.getAttribute('data-request-id');
                    const aid = wrap.getAttribute('data-attachment-id');
                    if (rid && aid) this.openDeleteFileModal(rid, aid);
                }
                resetSelect();
            }
        });

        // Delete file modal — confirm
        document.addEventListener('click', (e) => {
            if (e.target.id !== 'delete_file_confirm') return;
            e.preventDefault();
            const modal = document.getElementById('delete_file_modal');
            const rid   = modal?.dataset.requestId;
            const aid   = modal?.dataset.attachmentId;
            if (rid && aid) this.submitDeleteFile(rid, aid);
        });

        // Delete file modal — close / cancel
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#delete_file_modal')) return;
            if (e.target.closest('#delete_file_confirm')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeDeleteFileModal();
            }
        });

        // Edit file modal — submit
        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (form?.id !== 'edit_file_form') return;
            e.preventDefault();
            this.submitEditFile(form);
        });

        // Edit file modal — close / cancel
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#edit_file_modal')) return;
            if (e.target.closest('#edit_file_form button[type="submit"]')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeEditFileModal();
            }
        });

        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#edit_patient_details_btn');
            if (!btn || !this.details?.contains(btn)) return;
            e.preventDefault();
            this.openEditPatientDetailsModal();
        });

        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (form?.id !== 'edit_patient_details_form') return;
            e.preventDefault();
            this.submitEditPatientDetailsForm(form);
        });

        document.addEventListener('click', (e) => {
            if (!e.target.closest('#edit_patient_details_modal')) return;
            if (e.target.closest('#edit_patient_details_form button[type="submit"]')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeEditPatientDetailsModal();
            }
        });

        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#edit_case_manager_btn');
            if (!btn || !this.details?.contains(btn)) return;
            e.preventDefault();
            this.openEditCaseManagerModal();
        });

        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (form?.id !== 'edit_case_manager_form') return;
            e.preventDefault();
            this.submitEditCaseManagerForm(form);
        });

        document.addEventListener('click', (e) => {
            if (!e.target.closest('#edit_request_case_manager_modal')) return;
            if (e.target.closest('#edit_case_manager_form button[type="submit"]')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeEditCaseManagerModal();
            }
        });

        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#edit_roi_specialist_btn');
            if (!btn || !this.details?.contains(btn)) return;
            e.preventDefault();
            this.openEditRoiSpecialistModal();
        });

        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (form?.id !== 'edit_roi_specialist_form') return;
            e.preventDefault();
            this.submitEditRoiSpecialistForm(form);
        });

        document.addEventListener('click', (e) => {
            if (!e.target.closest('#edit_request_roi_specialist_modal')) return;
            if (e.target.closest('#edit_roi_specialist_form button[type="submit"]')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeEditRoiSpecialistModal();
            }
        });

        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (form?.id !== 'request_file_visibility_form') return;
            e.preventDefault();
            this.submitRequestFileVisibilityForm(form);
        });

        document.addEventListener('click', (e) => {
            if (!e.target.closest('#request_file_visibility_modal')) return;
            if (e.target.closest('#request_file_visibility_form button[type="submit"]')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeFileVisibilityModal();
            }
        });

        document.addEventListener('click', (e) => {
            const moreBtn = e.target.closest('.request-activity-more');
            if (!moreBtn) return;
            e.preventDefault();
            this.loadMoreActivity(moreBtn);
        });

        document.addEventListener('click', (e) => {
            const filterButton = e.target.closest('.request-section-filter');
            if (!filterButton || !this.details || !this.details.contains(filterButton)) return;
            e.preventDefault();
            const section = filterButton.getAttribute('data-request-section') || 'all';
            this.applyRequestSectionFilter(section);
        });


        document.addEventListener('click', (e) => {
            const crumb = e.target.closest('#breadcrumbs-one .breadcrumb-step');
            if (!crumb || !this.details?.contains(crumb)) return;
            const status = crumb.getAttribute('data-status');
            if (!status) return;
            e.preventDefault();
            this.activateRequestStepPanel(status);
        });

        document.addEventListener('click', (e) => {
            const btn = e.target.closest('.request_workflow_step_form [data-workflow-action]');
            if (!btn || !this.details?.contains(btn)) return;
            if (btn.tagName === 'BUTTON' && btn.type === 'submit') return;
            const form = btn.closest('form');
            if (!form || !form.hasAttribute('data-workflow-step')) return;
            e.preventDefault();
            const workflowAction = btn.getAttribute('data-workflow-action') || 'log_status';
            if (workflowAction === 'log_status') {
                this.openLogStatusModal(form, btn);
            } else if (workflowAction === 'generate_invoice') {
                this.openInvoiceForm(form, btn);
            } else {
                this.submitWorkflowForm(form, workflowAction, btn);
            }
        });

        // Reset invoice button — open confirmation modal
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#reset_invoice_btn');
            if (!btn || !this.details?.contains(btn)) return;
            e.preventDefault();
            this.openResetInvoiceModal(btn);
        });

        // Missing records-to-download modal actions
        document.addEventListener('click', (e) => {
            if (e.target.closest('#missing_records_download_modal .close_modal') || e.target.closest('#missing_records_download_modal .overlay')) {
                if (!e.target.closest('#missing_records_download_modal')) return;
                e.preventDefault();
                this.closeMissingRecordsDownloadModal();
                return;
            }
            const checkBtn = e.target.closest('#missing_records_download_check');
            if (checkBtn) {
                e.preventDefault();
                this.closeMissingRecordsDownloadModal();
                const filesBox = this.details?.querySelector('#files_box');
                filesBox?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
            }
            const uploadBtn = e.target.closest('#missing_records_download_upload');
            if (!uploadBtn) return;
            e.preventDefault();
            this.closeMissingRecordsDownloadModal();
            const trigger = this.details?.querySelector('[data-request-upload-trigger]') || document.getElementById('request_upload_btn');
            trigger?.click();
        });

        // Sync QB Payment button
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#sync_qb_payment_btn');
            if (!btn || !this.details?.contains(btn)) return;
            e.preventDefault();
            this.syncQbPayment(btn);
        });

        // Reset invoice modal — confirm
        document.addEventListener('click', (e) => {
            if (e.target.id !== 'reset_invoice_confirm') return;
            e.preventDefault();
            const confirmBtn = document.getElementById('reset_invoice_confirm');
            this.resetInvoice(confirmBtn);
        });

        // Reset invoice modal — close
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#reset_invoice_modal')) return;
            if (e.target.id === 'reset_invoice_confirm') return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeResetInvoiceModal();
            }
        });

        // Delete invoice button (admin-only) — open confirmation modal
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#delete_invoice_btn');
            if (!btn || !this.details?.contains(btn)) return;
            e.preventDefault();
            this.openDeleteInvoiceModal(btn);
        });

        // Delete invoice modal — confirm
        document.addEventListener('click', (e) => {
            if (e.target.id !== 'delete_invoice_confirm') return;
            e.preventDefault();
            const confirmBtn = document.getElementById('delete_invoice_confirm');
            this.deleteInvoice(confirmBtn);
        });

        // Delete invoice modal — close
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#delete_invoice_modal')) return;
            if (e.target.id === 'delete_invoice_confirm') return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeDeleteInvoiceModal();
            }
        });

        // Workflow notice modal — close (OK button, close icon, overlay)
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#workflow_notice_modal')) return;
            if (e.target.id === 'workflow_notice_modal_ok' || e.target.closest('.close_modal') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeWorkflowNoticeModal();
            }
        });

        // Log Status modal — confirm
        document.addEventListener('click', (e) => {
            if (e.target.id !== 'log_status_confirm') return;
            e.preventDefault();
            const confirmBtn = e.target;
            const noteEl     = document.getElementById('log_status_note');
            const errorEl    = document.getElementById('log_status_modal_error');
            const note       = noteEl?.value?.trim() || '';
            if (!note) {
                noteEl?.focus();
                noteEl?.classList.add('input-error');
                noteEl?.addEventListener('input', () => noteEl.classList.remove('input-error'), { once: true });
                return;
            }
            if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
            this.setBtnLoading(confirmBtn, true);
            this.submitWorkflowFormFromModal(this._logStatusForm, 'log_status', note, confirmBtn, errorEl);
        });

        // Log Status modal — close
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#log_status_modal')) return;
            if (e.target.closest('#log_status_confirm')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeLogStatusModal();
            }
        });

        // Invoice form — actions
        document.addEventListener('click', (e) => {
            const target = e.target;
            if (target.id === 'invoice-form-confirm') {
                e.preventDefault();
                this.submitInvoiceForm(target);
            } else if (target.id === 'invoice-form-cancel' || target.id === 'invoice-form-close') {
                e.preventDefault();
                this.closeInvoiceForm();
            } else if (target.id === 'invoice-form-add-row') {
                e.preventDefault();
                const container = this.details?.querySelector('#invoice-form-inline');
                if (container) {
                    const services = JSON.parse(container.dataset.services || '[]');
                    this.addInvoiceFormRow(container.querySelector('#invoice-form-rows'), services, {});
                    this.updateInvoiceFormTotal(container);
                }
            } else if (target.closest?.('.invoice-row-remove')) {
                e.preventDefault();
                const row       = target.closest('tr');
                const container = target.closest('#invoice-form-inline');
                row?.remove();
                if (container) this.updateInvoiceFormTotal(container);
            }
        });

        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (!form.matches?.('form[data-workflow-step]')) return;
            if (!this.details?.contains(form)) return;
            e.preventDefault();
            const submitter  = e.submitter instanceof HTMLButtonElement ? e.submitter : null;
            const action     = submitter?.getAttribute?.('data-workflow-action') || 'mark_complete';
            this.submitWorkflowForm(form, action, submitter);
        });

        document.addEventListener('change', (e) => {
            if (e.target && e.target.id === 'follow_up_date') {
                if (!this.activeRequestId) { console.warn('No active request ID'); return; }
                // Convert MM/DD/YYYY → YYYY-MM-DD for server storage.
                // Only send when fully entered (MM/DD/YYYY = 10 chars).
                const raw      = e.target.value.trim();
                const mdyMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (!mdyMatch) return;
                const dateVal  = `${mdyMatch[3]}-${mdyMatch[1]}-${mdyMatch[2]}`;
                this._followUpDateUpdated = true;
                this.updateFollowUpDate(this.activeRequestId, dateVal);
            }
            if (e.target && e.target.id === 'request_submitted_date') {
                if (!this.activeRequestId) return;
                const raw = e.target.value.trim();
                const m   = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (!m) return;
                this.updateRequestSubmittedDate(this.activeRequestId, `${m[3]}-${m[1]}-${m[2]}`);
            }
            if (e.target && e.target.id === 'request_resubmitted_date') {
                if (!this.activeRequestId) return;
                const raw = e.target.value.trim();
                const m   = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (!m) return;
                this.updateRequestResubmittedDate(this.activeRequestId, `${m[3]}-${m[1]}-${m[2]}`);
            }
            if (e.target && e.target.id === 'sub_status') {
                if (!this.activeRequestId) { console.warn('No active request ID'); return; }
                const subStatus = e.target.value;
                this.updateRequestSubStatus(this.activeRequestId, subStatus);
                e.target.dataset.prevValue = subStatus;
            }
            if (e.target && e.target.id === 'request_record_type') {
                if (!this.activeRequestId) { console.warn('No active request ID'); return; }
                this.updateRequestRecordType(this.activeRequestId, e.target.value, e.target);
            }
            if (e.target && e.target.id === 'request_affidavit') {
                if (!this.activeRequestId) { console.warn('No active request ID'); return; }
                this.updateRequestAffidavit(this.activeRequestId, e.target.value, e.target);
            }
        });

        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#awaiting_cm_review, .awaiting_cm_review_button');
            if (!btn) return;
            e.preventDefault();
            if (!this.activeRequestId) return;
            this.openReasonForCaseManagerModal();
        });

        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#undo_awaiting_cm_review_btn, .undo_awaiting_cm_review_button');
            if (!btn) return;
            e.preventDefault();
            if (!this.activeRequestId) return;
            this.undoAwaitingCMReview(this.activeRequestId, btn);
        });

        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#button_action_complete');
            if (!btn) return;
            e.preventDefault();
            if (!this.activeRequestId) return;
            this.undoAwaitingCMReview(this.activeRequestId, btn, 'Case Manager confirmed the request details issue has been resolved.');
        });

        document.addEventListener('click', (e) => {
            const genBtn = e.target.closest('#generate_request_documents_btn');
            if (!genBtn) return;
            e.preventDefault();
            this.openGenerateRequestDocumentsModal();
        });

        // Records-to-download: mark downloaded inline
        document.addEventListener('click', (e) => {
            const btn          = e.target.closest('.download-records-btn');
            if (!btn) return;
            const attachmentId = btn.dataset.attachmentId;
            const requestId    = btn.dataset.requestId;
            const nonce        = btn.dataset.nonce;
            if (!attachmentId || !requestId || !nonce) return;
            const body = new URLSearchParams();
            body.append('action', 'docuswift_mark_record_downloaded');
            body.append('request_id', requestId);
            body.append('attachment_id', attachmentId);
            body.append('nonce', nonce);
            fetch(this.ajaxUrl, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() })
                .then((r) => r.json())
                .then((res) => {
                    if (res.success) {
                        this.refreshActivityLists(res.data || {});
                        this.syncPipelineFromResponse(res.data || {});
                        this.fetchDetails(String(requestId));
                    }
                })
                .catch(() => {});
        });

        document.addEventListener('click', (e) => {
            if (e.target.id === 'generate_request_documents_confirm') {
                e.preventDefault();
                this.submitGenerateRequestDocuments();
                return;
            }
            if (!e.target.closest('#generate_request_documents_modal')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeGenerateRequestDocumentsModal();
            }
        });

        document.addEventListener('click', (e) => {
            const escBtn = e.target.closest('#escalated');
            if (!escBtn) return;
            e.preventDefault();
            if (!this.activeRequestId) return;
            this.openEscalateModal();
        });

        document.addEventListener('click', (e) => {
            const undoBtn = e.target.closest('#undo_escalate_btn');
            if (!undoBtn) return;
            e.preventDefault();
            if (!this.activeRequestId) return;
            this.undoEscalateRequest(this.activeRequestId);
        });

        document.addEventListener('click', (e) => {
            if (e.target.id !== 'escalate_request_confirm') return;
            e.preventDefault();
            const noteEl = document.getElementById('escalate_note');
            const note   = noteEl?.value.trim() || '';
            if (!note) {
                noteEl?.focus();
                noteEl?.classList.add('input-error');
                noteEl?.addEventListener('input', () => noteEl.classList.remove('input-error'), { once: true });
                return;
            }
            this.escalateRequest(this.activeRequestId, note);
        });

        document.addEventListener('click', (e) => {
            if (!e.target.closest('#escalate_request_modal')) return;
            if (e.target.closest('#escalate_request_confirm')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeEscalateModal();
            }
        });

        // Report a Problem modal
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#report_problem_btn');
            if (!btn) return;
            e.preventDefault();
            if (!this.activeRequestId) return;
            this.openReportProblemModal();
        });

        document.addEventListener('click', (e) => {
            if (e.target.id !== 'report_problem_confirm') return;
            e.preventDefault();
            const messageEl = document.getElementById('report_problem_message');
            const errorEl   = document.getElementById('report_problem_error');
            const message   = messageEl?.value.trim() || '';
            if (!message) {
                messageEl?.focus();
                messageEl?.classList.add('input-error');
                if (errorEl) { errorEl.textContent = 'Please describe the problem.'; errorEl.style.display = 'block'; }
                messageEl?.addEventListener('input', () => {
                    messageEl.classList.remove('input-error');
                    if (errorEl) errorEl.style.display = 'none';
                }, { once: true });
                return;
            }
            this.reportProblem(this.activeRequestId, message);
        });

        document.addEventListener('click', (e) => {
            if (!e.target.closest('#report_problem_modal')) return;
            if (e.target.closest('#report_problem_confirm')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeReportProblemModal();
            }
        });

        // Reason for case manager modal — confirm
        document.addEventListener('click', (e) => {
            if (e.target.id !== 'reason_for_case_manager_confirm') return;
            e.preventDefault();
            const confirmBtn = e.target;
            const errorEl    = document.getElementById('reason_for_case_manager_error');
            const hideError  = () => { if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; } };
            hideError();
            const select      = document.getElementById('reason_for_case_manager_select');
            const reason      = select?.value || '';
            if (!reason) {
                select?.focus();
                select?.classList.add('input-error');
                select?.addEventListener('change', () => select.classList.remove('input-error'), { once: true });
                return;
            }
            const reasonLabel = select.options[select.selectedIndex]?.textContent?.trim() || reason;
            const noteEl      = document.getElementById('reason_for_case_manager_note');
            const note        = noteEl?.value?.trim() || '';
            if (!note) {
                noteEl?.focus();
                noteEl?.classList.add('input-error');
                noteEl?.addEventListener('input', () => noteEl.classList.remove('input-error'), { once: true });
                return;
            }
            this.setAwaitingCMReview(this.activeRequestId, reason, note, reasonLabel, confirmBtn, errorEl);
        });

        // Reason for case manager modal — cancel / close
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#reason_for_case_manager_modal')) return;
            if (e.target.closest('#reason_for_case_manager_confirm')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeReasonForCaseManagerModal();
            }
        });

        // Set rush flag via sidebar select
        document.addEventListener('change', (e) => {
            if (e.target && e.target.id === 'is_rush_select') {
                if (!this.activeRequestId) { console.warn('No active request ID'); return; }
                const value = e.target.value;
                const prevValue = e.target.dataset.prevValue || '0';
                if (value === '' || value === prevValue) return;
                this.setRequestRush(this.activeRequestId, value, e.target);
            }
        });

        // Cancel request
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#csancel_request_btn');
            if (!btn) return;
            if (btn.classList.contains('cancel--pending')) return;
            e.preventDefault();
            this.openCancelRequestModal();
        });

        document.addEventListener('click', (e) => {
            if (e.target.id !== 'cancel_request_confirm') return;
            e.preventDefault();
            const confirmBtn = e.target;
            const reasonEl   = document.getElementById('cancel_request_reason');
            const noteEl     = document.getElementById('cancel_request_note');
            const reason     = reasonEl?.value?.trim() || '';
            const note       = noteEl?.value?.trim()   || '';
            if (!reason) {
                reasonEl?.focus();
                reasonEl?.classList.add('input-error');
                reasonEl?.addEventListener('input', () => reasonEl.classList.remove('input-error'), { once: true });
                return;
            }
            this.setBtnLoading(confirmBtn, true);
            this.cancelRequest(this.activeRequestId, reason, note, confirmBtn);
        });

        document.addEventListener('click', (e) => {
            if (!e.target.closest('#cancel_request_modal')) return;
            if (e.target.closest('#cancel_request_confirm')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeCancelRequestModal();
            }
        });

        document.addEventListener('click', (e) => {
            const acceptBtn  = e.target.closest('#cancel_request_accept');
            const declineBtn = e.target.closest('#cancel_request_decline');
            if (!acceptBtn && !declineBtn) return;
            e.preventDefault();
            const cancelIndex = parseInt((acceptBtn || declineBtn).dataset.cancelIndex ?? '0', 10);
            if (acceptBtn) {
                this.openResolveCancelModal('accept', cancelIndex);
            } else {
                this._declineCancelIndex = cancelIndex;
                this.openInvoiceForm();
            }
        });

        document.addEventListener('click', (e) => {
            if (e.target.id !== 'resolve_cancel_confirm') return;
            e.preventDefault();
            const confirmBtn = e.target;
            const noteEl     = document.getElementById('resolve_cancel_note');
            const errorEl    = document.getElementById('resolve_cancel_modal_error');
            const note       = noteEl?.value?.trim() || '';
            if (!note) {
                noteEl?.focus();
                noteEl?.classList.add('input-error');
                noteEl?.addEventListener('input', () => noteEl.classList.remove('input-error'), { once: true });
                return;
            }
            if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
            const { _resolveCancelResolution: resolution, _resolveCancelIndex: cancelIndex } = this;
            this.resolveCancelRequest(this.activeRequestId, cancelIndex, resolution, confirmBtn, note);
        });

        document.addEventListener('click', (e) => {
            if (!e.target.closest('#resolve_cancel_modal')) return;
            if (e.target.closest('#resolve_cancel_confirm')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeResolveCancelModal();
            }
        });

        // Toggle prepay / postpay forms
        document.addEventListener('click', (e) => {
            const editIcon = e.target.closest('.edit-prepay');
            if (editIcon) {
                e.preventDefault();
                const clientItem = e.target.closest(this.detailsSelector);
                clientItem?.querySelector('.prepay_edit_form')?.classList.toggle('active');
            }
            const editPostPayIcon = e.target.closest('.edit-postpay');
            if (editPostPayIcon) {
                e.preventDefault();
                const clientItem = e.target.closest(this.detailsSelector);
                clientItem?.querySelector('.postpay_edit_form')?.classList.toggle('active');
            }
        });

        document.addEventListener('submit', (e) => {
            if (e.target.matches(this.formSelector)) { e.preventDefault(); this.handlePrepaySubmit(e); }
        });

        document.addEventListener('submit', (e) => {
            if (e.target.matches(this.PostPayformSelector)) { e.preventDefault(); this.handlePostPaySubmit(e); }
        });

        document.addEventListener('submit', (e) => {
            if (e.target.matches(this.noteFormSelector)) { e.preventDefault(); this.handleNoteSubmit(e); }
        });

        document.addEventListener('click', (e) => {
            const deleteBtn = e.target.closest('.activity-delete-btn');
            if (deleteBtn) { e.preventDefault(); this.handleActivityDelete(deleteBtn); }

            const editBtn = e.target.closest('.activity-edit-btn');
            if (editBtn) { e.preventDefault(); this.handleActivityEdit(editBtn); }
        });

        // Facility search debounce
        document.addEventListener('input', (e) => {
            if (e.target.id !== 'confirm_facility_search') return;
            clearTimeout(this._facilitySearchTimer);
            const q = e.target.value;
            this._facilitySearchTimer = setTimeout(() => this.searchFacilities(q), this.facilitySearchDebounceMs);
        });

        // Facility results interaction
        document.addEventListener('click', (e) => {
            const editBtn = e.target.closest('.confirm-facility-result__edit');
            if (editBtn) {
                e.preventDefault();
                e.stopPropagation();
                const facilityId = parseInt(editBtn.dataset.facilityId, 10);
                if (!facilityId) return;
                this.confirmFacilityModal?.close();
                const facilityComponent = window.DocuswiftPortal?.getComponent('facility');
                facilityComponent?.setActiveRequest?.(this.activeRequestId);
                facilityComponent?.openEdit(facilityId);
                return;
            }

            const addNewLink = e.target.closest('#add_new_facility_link');
            if (addNewLink && addNewLink.closest('#confirm_facility_modal')) {
                e.preventDefault();
                e.stopPropagation();
                this.confirmFacilityModal?.close();
                const facilityComponent = window.DocuswiftPortal?.getComponent('facility');
                facilityComponent?.setActiveRequest?.(this.activeRequestId);
                facilityComponent?.openNew();
                return;
            }

            const deptHeader = e.target.closest('.confirm-facility-dept__header');
            if (deptHeader && deptHeader.closest('#confirm_facility_modal')) {
                e.preventDefault();
                e.stopPropagation();
                deptHeader.closest('.confirm-facility-dept')?.classList.toggle('is-expanded');
                return;
            }

            const header = e.target.closest('.confirm-facility-result__header');
            if (!header || !header.closest('#confirm_facility_modal')) return;
            e.preventDefault();
            const row    = header.closest('.confirm-facility-result');
            const id     = row?.dataset.facilityId || '';
            const hidden = document.getElementById('confirm_facility_selected_id');
            if (hidden) hidden.value = id;
            const wrap = document.getElementById('confirm_facility_results');
            wrap?.querySelectorAll('.confirm-facility-result').forEach((el) => { if (el !== row) el.classList.remove('is-selected'); });
            row?.classList.add('is-selected');
            row?.classList.toggle('is-expanded');
        });

        document.addEventListener('click', (e) => {
            if (e.target.id === 'confirm_facility_submit') { e.preventDefault(); this.submitConfirmFacility(); }
        });

        document.addEventListener('click', (e) => {
            if (e.target.id === 'confirm_facility_search_db') {
                e.preventDefault();
                const url = window.docuswiftPortal?.facilities_page_url || '/facilities/';
                window.location.href = url;
            }
        });
    }

    // ── Core ──────────────────────────────────────────────────────────────────

    fetchDetails(requestId) {
        const formData = new URLSearchParams();
        const nonce = window.docuswiftPortal?.nonce || '';
        formData.append('action', this.fetchAction);
        formData.append('request_id', requestId);
        formData.append('nonce', nonce);
        // Returning the promise lets callers `await` a real refresh instead of
        // racing ahead while this request is still in flight.
        return fetch(this.ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString(),
        })
            .then(async (response) => {
                if (!response.ok) {
                    const body = await response.text();
                    throw new Error(`Request details AJAX failed (${response.status}): ${body.slice(0, 200)}`);
                }
                return response.json();
            })
            .then(response => {
                if (!response?.success || !response?.data?.request_detail) {
                    const message = response?.data?.message || 'Could not load request details.';
                    this.showToast(message, 'error');
                    this.hide();
                    return;
                }
                this.details.innerHTML = response.data.request_detail;
                this.applyRequestSectionFilter('all');
                this.syncWorkflowButtonState();

                // Init flatpickr on any .date-picker inputs rendered in this panel.
                this.details.querySelectorAll('.date-picker').forEach(el => {
                    if (!el._flatpickr) {
                        DatePicker.initInput(el);
                    }
                });

                // Acquire lock after panel is rendered.
                this.acquireLock( requestId );

                document.dispatchEvent(new CustomEvent('docuswift:request-detail-rendered', {
                    detail: { requestId: String(requestId), root: this.details },
                }));
            })
            .catch((error) => {
                console.error(error);
                // Surface the failure instead of leaving the panel silently stale.
                this.showToast('Could not refresh the request panel. Please reload the page.', 'error');
            });
    }

    show(requestId) {
        this.activeRequestId = requestId;
        this._followUpDateUpdated = false;
        this._skipFollowUpCheck   = false;
        this.setRequestIdInUrl(requestId);
        if (this.details && this.skeletonMarkupKey && window[this.skeletonMarkupKey]) {
            this.details.innerHTML = window[this.skeletonMarkupKey];
        }
        if (this.overlay) this.overlay.classList.add('active');
        if (this.details) this.details.classList.add('active');
        if (this.details && this.overlay) document.body.style.overflow = 'hidden';
        this.fetchDetails(requestId);
    }

    getRequestIdFromUrl() {
        const params = new URLSearchParams(window.location.search || '');
        const rawId = (params.get('request-id') || params.get('request_id') || '').trim();
        if (!rawId) return null;

        const numericId = parseInt(rawId, 10);
        if (!Number.isInteger(numericId) || numericId <= 0) return null;

        return String(numericId);
    }

    openFromUrlIfPresent() {
        const requestId = this.getRequestIdFromUrl();
        if (!requestId) return;

        const registry = window.__docuswiftAutoOpenedRequestDetails || (window.__docuswiftAutoOpenedRequestDetails = {});
        const registryKey = `${this.detailsSelector}:${requestId}`;
        if (registry[registryKey]) return;
        registry[registryKey] = true;

        this.show(requestId);
        this.initRequestUploadModal();
        this.initConfirmFacilityModal();
    }

    setRequestIdInUrl(requestId) {
        const rid = String(requestId || '').trim();
        if (!rid) return;

        const url = new URL(window.location.href);
        if (url.searchParams.get('request-id') === rid) return;

        url.searchParams.delete('request_id');
        url.searchParams.set('request-id', rid);
        window.history.pushState({}, '', url.toString());
    }

    clearRequestIdFromUrl() {
        const url = new URL(window.location.href);
        const hasRequestId = url.searchParams.has('request-id') || url.searchParams.has('request_id');
        if (!hasRequestId) return;

        url.searchParams.delete('request-id');
        url.searchParams.delete('request_id');
        window.history.replaceState({}, '', url.toString());
    }

    hide() {
        const isRoi              = (window.docuswiftPortal?.current_user_roles || []).includes('roi_specialist');
        const reminderEnabled    = !!window.docuswiftPortal?.followUpReminderEnabled;
        console.log('[FUP]', { isRoi, reminderEnabled, followUpDateUpdated: this._followUpDateUpdated, skip: this._skipFollowUpCheck, requestId: this.activeRequestId });
        if (isRoi && reminderEnabled && !this._followUpDateUpdated && !this._skipFollowUpCheck && this.activeRequestId) {
            this._showFollowUpPrompt();
            return;
        }
        this.releaseLock();
        this._skipFollowUpCheck   = false;
        this._followUpDateUpdated = false;
        this.activeRequestId = null;
        if (this.overlay) this.overlay.classList.remove('active');
        if (this.details) {
            this.details.classList.remove('active');
            this.details.querySelector('.remove-skeleton')?.classList.remove('remove-skeleton');
        }
        document.body.style.overflow = '';
        this.clearRequestIdFromUrl();
        window.dispatchEvent(new CustomEvent('docuswift:requests-table-refresh'));
    }

    _showFollowUpPrompt() {
        const modal = document.getElementById('follow_up_prompt_modal');
        if (!modal) {
            if (window.confirm('No follow-up date was recorded. Close anyway?')) {
                this._skipFollowUpCheck = true;
                this.hide();
            }
            return;
        }
        Modal.open('follow_up_prompt_modal');

        const onUpdate = (e) => {
            e.preventDefault();
            Modal.close('follow_up_prompt_modal');
            cleanup();
            const fuInput = this.details?.querySelector('#follow_up_date');
            if (fuInput) {
                fuInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => fuInput.focus(), 300);
            }
        };
        const onClose = (e) => {
            e.preventDefault();
            Modal.close('follow_up_prompt_modal');
            cleanup();
            this._skipFollowUpCheck = true;
            this.hide();
        };

        const btnUpdate  = modal.querySelector('[data-fup-action="update"]');
        const btnCloseEls = Array.from( modal.querySelectorAll('[data-fup-action="close"]') );

        function cleanup() {
            btnUpdate?.removeEventListener('click', onUpdate);
            btnCloseEls.forEach(el => el.removeEventListener('click', onClose));
        }

        btnUpdate?.addEventListener('click', onUpdate);
        btnCloseEls.forEach(el => el.addEventListener('click', onClose));
    }

    isOpen() {
        return this.details?.classList.contains('active');
    }

    showToast(message, type = 'success', duration = 3500) {
        showToast(message, type, duration);
    }

    applyRequestSectionFilter(section) {
        if (!this.details) return;
        const selectedSection = (section || 'all').toLowerCase();
        const showAll         = selectedSection === 'all';
        const notesBox        = this.details.querySelector('#activities_box');
        const activityLogBox  = this.details.querySelector('#activity_box');
        const filesBox        = this.details.querySelector('#files_box');
        const paymentsBox     = this.details.querySelector('#payments_box');
        if (notesBox)       notesBox.hidden       = !(showAll || selectedSection === 'notes');
        if (activityLogBox) activityLogBox.hidden = !showAll;
        if (filesBox)       filesBox.hidden        = !(showAll || selectedSection === 'files');
        if (paymentsBox)    paymentsBox.hidden      = !(showAll || selectedSection === 'payments');

        this.details.querySelectorAll('.request-section-filter').forEach((button) => {
            const isActive = (button.getAttribute('data-request-section') || '').toLowerCase() === selectedSection;
            button.classList.toggle('active', isActive);
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
    }

    // ── Upload / Facility modals ──────────────────────────────────────────────

    initRequestUploadModal() {
        if (!this.uploadModal) {
            this.uploadModal = new RequestUploadModal('#request_upload_modal');
            this.uploadModal.init();
        }
    }

    initConfirmFacilityModal() {
        if (this.confirmFacilityModal) return;
        this.confirmFacilityModal = new ConfirmFacilityModal('#confirm_facility_modal');
        this.confirmFacilityModal.init();
    }

    // ── Edit Status modal ─────────────────────────────────────────────────────

    openEditStatusModal(btn) {
        const modal   = document.getElementById('edit_status_modal');
        const select  = document.getElementById('edit_status_select');
        const errEl   = document.getElementById('edit_status_modal_error');
        if (!modal || !select) return;

        const requestId     = btn.dataset.requestId     || this.activeRequestId;
        const currentStatus = btn.dataset.currentStatus || '';
        let   choices       = {};
        try { choices = JSON.parse(btn.dataset.statusChoices || '{}'); } catch (_) {}

        modal.dataset.requestId = requestId;

        // Destroy existing NiceSelect instance before repopulating
        if (select._niceSelect) {
            try { select._niceSelect.destroy(); } catch (_) {}
            delete select._niceSelect;
        }
        select.removeAttribute('data-nice-select');

        select.innerHTML = Object.entries(choices)
            .map(([key, label]) =>
                `<option value="${key}"${key === currentStatus ? ' selected' : ''}>${label}</option>`
            ).join('');

        // Re-init NiceSelect with fresh options
        new NiceSelect(select, { searchable: false });
        select.setAttribute('data-nice-select', 'true');

        const noteEl2 = document.getElementById('edit_status_note');
        if (noteEl2) { noteEl2.value = ''; noteEl2.classList.remove('input-error'); }
        if (errEl) { errEl.textContent = ''; errEl.style.display = 'none'; }
        Modal.open('edit_status_modal');
    }

    async submitEditStatus() {
        const modal      = document.getElementById('edit_status_modal');
        const select     = document.getElementById('edit_status_select');
        const noteEl     = document.getElementById('edit_status_note');
        const confirmBtn = document.getElementById('edit_status_confirm');
        const errEl      = document.getElementById('edit_status_modal_error');
        if (!modal || !select) return;

        const requestId = modal.dataset.requestId || this.activeRequestId;
        const status    = select.value || select.querySelector('option[selected]')?.value || select.options[0]?.value;
        const note      = (noteEl?.value || '').trim();
        const nonce     = window.docuswiftPortal?.nonce || '';

        if (!requestId || !status) return;

        if (!note) {
            if (errEl) { errEl.textContent = 'Note is required.'; errEl.style.display = 'block'; }
            noteEl?.classList.add('input-error');
            noteEl?.focus();
            return;
        }
        noteEl?.classList.remove('input-error');

        this.setBtnLoading(confirmBtn, true);
        if (errEl) errEl.style.display = 'none';

        try {
            const body = new URLSearchParams();
            body.append('action',     'update_request_status');
            body.append('nonce',      nonce);
            body.append('request_id', requestId);
            body.append('status',     status);
            body.append('note',       note);

            const res  = await fetch(this.ajaxUrl, {
                method:  'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body:    body.toString(),
            });
            const json = await res.json();

            if (!json.success) throw new Error(json.data?.message || 'Could not update status');

            Modal.close('edit_status_modal');
            if (noteEl) noteEl.value = '';

            // Full re-fetch: sidebar, request-info, workflow steps are PHP-rendered
            // conditionally based on status — partial DOM updates are not enough.
            await this.fetchDetails(String(requestId));

        } catch (err) {
            if (errEl) { errEl.textContent = err.message; errEl.style.display = 'block'; }
        } finally {
            this.setBtnLoading(confirmBtn, false);
        }
    }

    openRequestUploadModal() {
        this.initRequestUploadModal();
        const postPayValue = this.details.querySelector('.client_details_postpay .value')?.getAttribute('amount')?.trim().replace(/\s/g, '');
        const prePayValue  = this.details.querySelector('.client_details_prepay .value')?.getAttribute('amount')?.trim().replace(/\s/g, '');
        this.uploadModal.open(this.activeRequestId, { postPay: postPayValue, prePay: prePayValue });
    }

    async setRequestRush(requestId, value, selectEl) {
        if (!requestId || !this.ajaxUrl) return;

        const isRush = value === '1';
        const prevValue = selectEl?.dataset?.prevValue || '0';

        // Optimistically update the header badge so the UI feels instant.
        this.updateRushHeaderBadge(isRush);

        try {
            const result = await apiClient.post('set_request_rush', { request_id: String(requestId), value: isRush ? '1' : '0' });

            if (!result.success) {
                this.showToast(result.data?.message || 'Could not update rush status.', 'error');
                this.revertRushSelect(selectEl, prevValue);
                return;
            }

            const newIsRush = !!result.data?.is_rush;
            if (selectEl) selectEl.dataset.prevValue = newIsRush ? '1' : '0';
            this.updateRushHeaderBadge(newIsRush);
            this.refreshActivityLists(result.data || {});
            this.showToast(result.data?.message || 'Rush status updated.');
        } catch (err) {
            console.error(err);
            this.showToast('Could not update rush status.', 'error');
            this.revertRushSelect(selectEl, prevValue);
        }
    }

    revertRushSelect(selectEl, prevValue) {
        if (!selectEl) return;
        selectEl.value = prevValue;
        try {
            selectEl._niceSelect?.update?.();
        } catch (_) {
            // Fallback: nice-select may not be initialized.
        }
        this.updateRushHeaderBadge(prevValue === '1');
    }

    updateRushHeaderBadge(isRush) {
        const titlePrimary = this.details?.querySelector('.request-title-primary');
        if (!titlePrimary) return;

        let badge = titlePrimary.querySelector('.request-rush-badge');
        if (isRush) {
            if (!badge) {
                badge = document.createElement('span');
                badge.className = 'request-rush-badge';
                badge.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M13 2l.018 .001l.016 .001l.083 .005l.011 .002h.011l.038 .009l.052 .008l.016 .006l.011 .001l.029 .011l.052 .014l.019 .009l.015 .004l.028 .014l.04 .017l.021 .012l.022 .01l.023 .015l.031 .017l.034 .024l.018 .011l.013 .012l.024 .017l.038 .034l.022 .017l.008 .01l.014 .012l.036 .041l.026 .027l.006 .009c.12 .147 .196 .322 .218 .513l.001 .012l.002 .041l.004 .064v6h5a1 1 0 0 1 .868 1.497l-.06 .091l-8 11c-.568 .783 -1.808 .38 -1.808 -.588v-6h-5a1 1 0 0 1 -.868 -1.497l.06 -.091l8 -11l.01 -.013l.018 -.024l.033 -.038l.018 -.022l.009 -.008l.013 -.014l.04 -.036l.028 -.026l.008 -.006a1 1 0 0 1 .402 -.199l.011 -.001l.027 -.005l.074 -.013l.011 -.001l.041 -.002z" /></svg>Rush`;
                titlePrimary.appendChild(badge);
            }
        } else if (badge) {
            badge.remove();
        }
    }
}

// ── Apply mixins to prototype ─────────────────────────────────────────────────
Object.assign(
    RequestDetails.prototype,
    ModalsMixin,
    ActivityMixin,
    FilesMixin,
    WorkflowMixin,
    PaymentsMixin,
    FacilityMixin,
    LockMixin,
);
